import { useState, useMemo, useEffect } from 'react';
import { useProductsGrouped } from '../shared/hooks/useProducts';
import { ProductWithCalculations } from '../shared/types/inventory';
import { ProductDetailModal } from '../components/ProductDetailModal';
import { CNPJSection } from '../components/CNPJSection';
import { AlertCircle, RefreshCw, ChevronsDownUp, ChevronsUpDown, Building2 } from 'lucide-react';
import { SellerPageLoader } from '../shared/components/SellerPageLoader';

export const TablePage = () => {
  const { groups, allProducts, loading, error, refetch } = useProductsGrouped();
  const [selectedProduct, setSelectedProduct] = useState<ProductWithCalculations | null>(null);
  const [expandedCNPJs, setExpandedCNPJs] = useState<Set<string>>(new Set());

  const cnpjList = useMemo(() => groups.map((g) => g.cnpj), [groups]);

  useEffect(() => {
    if (groups.length === 1) {
      setExpandedCNPJs(new Set([groups[0].cnpj]));
    } else if (groups.length > 0 && expandedCNPJs.size === 0) {
      setExpandedCNPJs(new Set([groups[0].cnpj]));
    }
  }, [groups]);

  const allExpanded = expandedCNPJs.size === cnpjList.length && cnpjList.length > 0;
  const noneExpanded = expandedCNPJs.size === 0;

  const handleToggle = (cnpj: string) => {
    setExpandedCNPJs((prev) => {
      const next = new Set(prev);
      if (next.has(cnpj)) {
        next.delete(cnpj);
      } else {
        next.add(cnpj);
      }
      return next;
    });
  };

  const expandAll = () => setExpandedCNPJs(new Set(cnpjList));
  const collapseAll = () => setExpandedCNPJs(new Set());

  if (loading) {
    return (
      <SellerPageLoader
        title={'Preparando a tabela de reposi\u00e7\u00e3o'}
        description={'Calculando estoque, cobertura e sugest\u00f5es por CNPJ.'}
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-gray-900 font-semibold">Erro ao carregar dados</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tabela de Reposição</h1>
          <p className="text-gray-600">
            Análise detalhada dos produtos com sugestões de pedido baseadas em dados
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building2 className="w-4 h-4 text-gray-400" />
            <span className="font-medium">{groups.length}</span>
            <span>{groups.length === 1 ? 'CNPJ' : 'CNPJs'}</span>
            <span className="text-gray-300 mx-1">|</span>
            <span className="font-medium">{allProducts.length}</span>
            <span>produtos no total</span>
          </div>
        </div>

        {groups.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              disabled={allExpanded}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              <ChevronsUpDown className="w-4 h-4" />
              Expandir todos
            </button>
            <button
              onClick={collapseAll}
              disabled={noneExpanded}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              <ChevronsDownUp className="w-4 h-4" />
              Ocultar todos
            </button>
          </div>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Nenhum dado encontrado</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group:any) => (
            <CNPJSection
              key={group.cnpj}
              cnpj={group.cnpj}
              razaoSocial={group.razaoSocial}
              products={group.products}
              isExpanded={expandedCNPJs.has(group.cnpj)}
              onToggle={() => handleToggle(group.cnpj)}
              onSelectProduct={setSelectedProduct}
            />
          ))}
        </div>
      )}

      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Dica:</strong> Clique nos cabeçalhos para ordenar e arraste a divisória direita de qualquer coluna para ajustar sua largura. Clique em qualquer produto para ver a visualização detalhada.
        </p>
      </div>

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};
